import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import AppPaths from '../../AppPaths';
import Button from '../../components/Button';
import ErrorMessage, { useError } from '../../components/ErrorMessage';
import { sendPasswordResetEmail } from '../../lib/authentication';
import ErrorMessages, { logInternalError } from '../../lib/errors';
import styles from './ForgotPassword.module.scss';
import { isAxiosError } from '../../lib/fi-api/apiUtils';
import { validateEmail } from '../../lib/util';

function ForgotPasswordForm() {
  const { t } = useTranslation();
  const history = useHistory();
  const { error, errorID, setError } = useError();
  const [submitting, setSubmitting] = React.useState(false);
  const [success, setSuccess] = React.useState(false);

  const handleLogin = useCallback(
    (event: any) => {
      event.preventDefault();
      const email = event.target.email.value.trim();
      const missingEmailError = t('please_enter_a_valid_email_address');

      if (email && validateEmail(email)) {
        sendPasswordResetEmail(email)
          .then(() => {
            setSuccess(true);
          })
          .catch((err) => {
            if (isAxiosError(err) && err.response?.status === 400) {
              // The password reset endpoint should swallow all errors to avoid providing any indication of account
              // validity. If it's a 400 it should only be due to a blank email address.
              setError(missingEmailError);
            } else {
              logInternalError(err);
              setError(ErrorMessages.INPUT_ERROR);
            }
          })
          .finally(() => {
            setSubmitting(false);
          });
      } else {
        setError(missingEmailError);
      }
    },
    [setError, t],
  );

  if (success) {
    return (
      <>
        <p>{t('check_your_email_for_a_link_to_reset_your_password')}</p>
        <Button type="button" onClick={() => history.push(AppPaths.Home)}>
          {t('return_to_store')}
        </Button>
      </>
    );
  }

  return (
    <>
      <form onSubmit={handleLogin} className={styles.forgotPasswordForm}>
        <div className={styles.formSection}>
          <input type="text" name="email" placeholder={t('email_address')} />
          <Button type="submit" disabled={submitting}>
            {t('submit')}
          </Button>
        </div>
        {error && <ErrorMessage errors={[error]} errorID={errorID} />}
      </form>
      <Link to={AppPaths.Login}>{t('nevermind_take_me_back')}</Link>
    </>
  );
}

export default function ForgotPassword() {
  const { t } = useTranslation();
  return (
    <div className={styles.container}>
      <h2>{t('forgot_password')}</h2>
      <ForgotPasswordForm />
    </div>
  );
}
